import React from 'react';
import { Calculator, Pin, PinOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Position = { x: number; y: number };
type CalcHistoryItem = {
  id: string;
  expression: string;
  result: string;
};

const OPEN_KEY = 'accounting_floating_calculator_open_v1';
const PIN_KEY = 'accounting_floating_calculator_pin_v1';
const POS_KEY = 'accounting_floating_calculator_pos_v1';
const HISTORY_KEY = 'accounting_floating_calculator_history_v1';

function safeEval(expr: string): number {
  const sanitized = expr.replace(/[^0-9+\-*/().%]/g, '');
  if (!sanitized.trim()) return 0;
  // eslint-disable-next-line no-new-func
  const out = Function(`"use strict"; return (${sanitized});`)();
  const n = Number(out);
  return Number.isFinite(n) ? n : 0;
}

function formatNumberToken(token: string): string {
  if (!token) return token;
  const [intPart, decPart] = token.split('.');
  const sign = intPart.startsWith('-') ? '-' : '';
  const baseInt = sign ? intPart.slice(1) : intPart;
  const withCommas = baseInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withCommas}${decPart !== undefined ? `.${decPart}` : ''}`;
}

function formatExpressionDisplay(expr: string): string {
  return expr.replace(/-?\d+(\.\d+)?/g, (m) => formatNumberToken(m));
}

const FloatingCalculator: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [pinned, setPinned] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(PIN_KEY) !== '0';
  });
  const [pos, setPos] = React.useState<Position>(() => {
    if (typeof window === 'undefined') return { x: 24, y: 80 };
    try {
      const raw = window.localStorage.getItem(POS_KEY);
      if (!raw) return { x: 24, y: 80 };
      return JSON.parse(raw) as Position;
    } catch {
      return { x: 24, y: 80 };
    }
  });
  const [display, setDisplay] = React.useState('0');
  const [history, setHistory] = React.useState<CalcHistoryItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(HISTORY_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as CalcHistoryItem[];
    } catch {
      return [];
    }
  });
  const [active, setActive] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const dragRef = React.useRef<{ dx: number; dy: number; dragging: boolean }>({
    dx: 0,
    dy: 0,
    dragging: false,
  });
  const shellRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  }, [open]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PIN_KEY, pinned ? '1' : '0');
  }, [pinned]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(POS_KEY, JSON.stringify(pos));
  }, [pos]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 30)));
  }, [history]);

  const insertAtCursor = React.useCallback((text: string) => {
    const input = inputRef.current;
    if (!input) {
      setDisplay((prev) => (prev === '0' || prev === 'Error' ? text : prev + text));
      return;
    }
    const start = input.selectionStart ?? display.length;
    const end = input.selectionEnd ?? start;
    const current = display === 'Error' ? '0' : display;
    const base = current === '0' ? '' : current;
    const next = base.slice(0, start) + text + base.slice(end);
    setDisplay(next || '0');
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + text.length;
      input.setSelectionRange(pos, pos);
    });
  }, [display]);

  const deleteBackwardAtCursor = React.useCallback(() => {
    const input = inputRef.current;
    if (!input) {
      setDisplay((prev) => (prev.length <= 1 ? '0' : prev.slice(0, -1)));
      return;
    }
    const start = input.selectionStart ?? display.length;
    const end = input.selectionEnd ?? start;
    const current = display === 'Error' ? '0' : display;
    if (start !== end) {
      const next = current.slice(0, start) + current.slice(end);
      setDisplay(next || '0');
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(start, start);
      });
      return;
    }
    if (start <= 0) return;
    const next = current.slice(0, start - 1) + current.slice(end);
    setDisplay(next || '0');
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start - 1, start - 1);
    });
  }, [display]);

  React.useEffect(() => {
    if (!open || !active) return;
    const onKey = (e: KeyboardEvent) => {
      if (!open) return;
      const k = e.key;
      if (/^[0-9]$/.test(k)) {
        insertAtCursor(k);
      } else if (['+', '-', '*', '/', '.', '(', ')', '%'].includes(k)) {
        insertAtCursor(k);
      } else if (k === 'Backspace') {
        deleteBackwardAtCursor();
      } else if (k === 'Delete') {
        const input = inputRef.current;
        if (!input) {
          setDisplay('0');
        } else {
          const start = input.selectionStart ?? display.length;
          const end = input.selectionEnd ?? start;
          const current = display === 'Error' ? '0' : display;
          const next = current.slice(0, start) + current.slice(end + (start === end ? 1 : 0));
          setDisplay(next || '0');
          requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(start, start);
          });
        }
      } else if (k === 'Escape') {
        setDisplay('0');
      } else if (k === 'Enter' || k === '=') {
        run();
      } else {
        return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, deleteBackwardAtCursor, display, insertAtCursor, open]);

  const append = (v: string) => insertAtCursor(v);

  const run = () => {
    try {
      const expression = display;
      const result = safeEval(expression);
      const resultText = String(result);
      setDisplay(resultText);
      setHistory((prev) => [
        { id: `${Date.now()}`, expression, result: resultText },
        ...prev,
      ]);
    } catch {
      setDisplay('Error');
    }
  };

  const clear = () => setDisplay('0');
  const backspace = () => deleteBackwardAtCursor();

  const onDragStart: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (pinned) return;
    const rect = shellRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { dragging: true, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.dragging || pinned) return;
      setPos({
        x: Math.max(8, e.clientX - dragRef.current.dx),
        y: Math.max(8, e.clientY - dragRef.current.dy),
      });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [pinned]);

  if (!open) return null;

  return (
    <div
      ref={shellRef}
      tabIndex={0}
      dir="ltr"
      onMouseDown={() => setActive(true)}
      onBlur={() => setActive(false)}
      className={`fixed z-[70] w-[360px] rounded-xl border bg-white shadow-2xl outline-none ${
        active ? 'ring-2 ring-amber-400' : ''
      }`}
      style={
        pinned
          ? { left: 20, bottom: 20 }
          : {
              left: pos.x,
              top: pos.y,
            }
      }
    >
      <div
        onMouseDown={onDragStart}
        className={`flex items-center justify-between rounded-t-xl px-3 py-2 text-white ${
          pinned ? 'cursor-default bg-slate-800' : 'cursor-move bg-slate-800'
        }`}
        dir="rtl"
      >
        <div className="flex items-center gap-2 text-sm">
          <Calculator className="h-4 w-4" />
          ماشین حساب
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-white hover:bg-white/10"
            onClick={() => setPinned((v) => !v)}
            title={pinned ? 'آزادسازی' : 'پین در گوشه'}
          >
            {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-white hover:bg-white/10"
            onClick={() => onOpenChange(false)}
            title="بستن"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2 p-3" dir="ltr">
        <div className="rounded-md border bg-slate-50 px-3 py-2 text-left font-mono" dir="ltr">
          <input
            ref={inputRef}
            value={display}
            onFocus={() => setActive(true)}
            onChange={(e) => {
              const raw = e.target.value.replace(/,/g, '');
              setDisplay(raw || '0');
            }}
            className="w-full border-0 bg-transparent text-xs text-slate-500 outline-none"
            dir="ltr"
          />
          <div className="text-xs text-slate-500">{formatExpressionDisplay(display)}</div>
          <div className="mt-1 text-2xl">{formatNumberToken(display)}</div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <Button type="button" variant="outline" onClick={clear}>C</Button>
          <Button type="button" variant="outline" onClick={() => append('(')}>(</Button>
          <Button type="button" variant="outline" onClick={() => append(')')}>)</Button>
          <Button type="button" variant="outline" onClick={backspace}>⌫</Button>
          <Button type="button" variant="outline" onClick={() => append('/')}>÷</Button>

          <Button type="button" variant="outline" onClick={() => append('7')}>7</Button>
          <Button type="button" variant="outline" onClick={() => append('8')}>8</Button>
          <Button type="button" variant="outline" onClick={() => append('9')}>9</Button>
          <Button type="button" variant="outline" onClick={() => append('*')}>×</Button>

          <Button type="button" variant="outline" onClick={() => append('4')}>4</Button>
          <Button type="button" variant="outline" onClick={() => append('5')}>5</Button>
          <Button type="button" variant="outline" onClick={() => append('6')}>6</Button>
          <Button type="button" variant="outline" onClick={() => append('-')}>-</Button>

          <Button type="button" variant="outline" onClick={() => append('1')}>1</Button>
          <Button type="button" variant="outline" onClick={() => append('2')}>2</Button>
          <Button type="button" variant="outline" onClick={() => append('3')}>3</Button>
          <Button type="button" variant="outline" onClick={() => append('+')}>+</Button>

          <Button type="button" variant="outline" onClick={() => append('%')}>%</Button>
          <Button type="button" variant="outline" onClick={() => append('0')}>0</Button>
          <Button type="button" variant="outline" onClick={() => append('.')}>.</Button>
          <Button type="button" onClick={run}>=</Button>
        </div>

        <div className="rounded-md border">
          <div className="flex items-center justify-between border-b px-3 py-2 text-xs text-slate-600">
            <span>تاریخچه</span>
            <button
              type="button"
              className="text-rose-600"
              onClick={() => setHistory([])}
            >
              پاک کردن
            </button>
          </div>
          <div className="max-h-36 overflow-y-auto p-2">
            {history.length === 0 ? (
              <div className="text-center text-xs text-slate-400">تاریخچه‌ای ثبت نشده</div>
            ) : (
              history.slice(0, 10).map((h) => (
                <div key={h.id} className="mb-1 rounded border px-2 py-1 text-xs">
                  <div className="font-mono text-slate-600" dir="ltr">{formatExpressionDisplay(h.expression)}</div>
                  <div className="font-mono" dir="ltr">= {formatNumberToken(h.result)}</div>
                  <div className="mt-1 flex gap-1">
                    <button
                      type="button"
                      className="rounded border px-1.5 py-0.5"
                      onClick={() => setDisplay(h.expression)}
                    >
                      بازگردانی فرمول
                    </button>
                    <button
                      type="button"
                      className="rounded border px-1.5 py-0.5"
                      onClick={() => setDisplay(h.result)}
                    >
                      بازگردانی نتیجه
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export function readInitialCalculatorOpen(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(OPEN_KEY) === '1';
}

export default FloatingCalculator;
